import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import { fetchRetentionSettings, updateRetentionSettings, fetchTags, createTag, updateTag, deleteTag, fetchTrashImpact } from '../../../store/actions/FolderAction';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import Badge from '../../shared/TailwindComponents/Badge';
import { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';
import { PiPlusLight, PiTrashLight, PiPencilSimpleLight, PiWarningCircleLight, PiShieldWarningLight } from 'react-icons/pi';
import Input from '../../shared/TailwindComponents/Input';
import Button from '../../shared/TailwindComponents/Button';
import { containsDisallowedCharacters } from '../../../utils/validation';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, orgSettingsTourSteps } from '../../shared/FeatureTour';

const RETENTION_OPTIONS = [
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' },
  { value: '365', label: '365 days' },
];

const TAG_COLORS: { value: BadgeColor; label: string }[] = [
  { value: 'blue', label: 'Blue' },
  { value: 'green', label: 'Green' },
  { value: 'purple', label: 'Purple' },
  { value: 'orange', label: 'Orange' },
  { value: 'red', label: 'Red' },
  { value: 'cyan', label: 'Cyan' },
  { value: 'indigo', label: 'Indigo' },
  { value: 'pink', label: 'Pink' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'emerald', label: 'Emerald' },
  { value: 'violet', label: 'Violet' },
  { value: 'amber', label: 'Amber' },
  { value: 'gray', label: 'Gray' },
];

interface OrgTag {
  id: number;
  uuid: string;
  name: string;
  color: string;
  is_system: boolean;
  file_count?: number;
  folder_count?: number;
}

const OrganizationSettings: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const isAdmin = !!userInfo?.is_org_admin;

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'org-settings',
    steps: orgSettingsTourSteps,
    autoStartDelay: 600,
    readyToShow: isAdmin,
  });

  const [retentionDays, setRetentionDays] = useState<string>('30');
  const [tags, setTags] = useState<OrgTag[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState<string>('blue');
  const [editingTag, setEditingTag] = useState<OrgTag | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState<string>('blue');

  const loadTags = useCallback(async () => {
    const result = await dispatch(fetchTags() as any);
    setTags(result || []);
  }, [dispatch]);

  useEffect(() => {
    const loadSettings = async () => {
      const days = await dispatch(fetchRetentionSettings() as any);
      setRetentionDays(String(days));
    };
    loadSettings();
    loadTags();
  }, [dispatch, loadTags]);

  const handleRetentionChange = async (selectedValue: string | string[]) => {
    const value = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;
    const newDays = parseInt(value, 10);
    const currentDays = parseInt(retentionDays, 10);

    if (newDays < currentDays) {
      const impact = await dispatch(fetchTrashImpact(newDays) as any);
      if (impact && impact.would_expire > 0) {
        dispatch(showModal({
          type: 'confirmation',
          message: t('Reduce Auto-delete Period'),
          subMsg: t('Reducing the auto-delete period to {{days}} days will cause {{count}} items currently in the trash to be permanently deleted on the next cleanup cycle. This cannot be undone.', {
            days: newDays,
            count: impact.would_expire,
          }),
          icon: <PiWarningCircleLight className="w-6 h-6 text-amber-500" />,
          isDest: true,
          onConfirm: async () => {
            setRetentionDays(value);
            await dispatch(updateRetentionSettings(newDays) as any);
            dispatch(hideModal());
          },
          onCancel: () => {},
          showModal: true,
        }));
        return;
      }
    }

    setRetentionDays(value);
    await dispatch(updateRetentionSettings(newDays) as any);
  };

  const [tagError, setTagError] = useState('');

  const handleCreateTag = async () => {
    const trimmed = newTagName.trim();
    if (!trimmed) return;
    if (trimmed.length > 50) {
      setTagError(t('Tag name must be between 1 and 50 characters'));
      return;
    }
    // Client-side duplicate check (case-insensitive)
    const duplicate = tags.find(t => t.name.toLowerCase() === trimmed.toLowerCase());
    if (duplicate) {
      setTagError(t('A tag with this name already exists'));
      return;
    }
    const result = await dispatch(createTag(trimmed, newTagColor) as any);
    if (result?._error === 'duplicate') {
      setTagError(t('A tag with this name already exists'));
      return;
    }
    if (result && result.id) {
      setNewTagName('');
      setNewTagColor('blue');
      setTagError('');
      await loadTags();
    }
  };

  const handleDeleteTag = async (tag: OrgTag) => {
    const totalUsage = (tag.file_count || 0) + (tag.folder_count || 0);

    if (totalUsage > 0) {
      dispatch(showModal({
        type: 'confirmation',
        message: t('Delete Tag'),
        subMsg: t('This tag is linked to {{fileCount}} file(s) and {{folderCount}} folder(s). Deleting it will remove the tag from all of them. This cannot be undone.', {
          fileCount: tag.file_count || 0,
          folderCount: tag.folder_count || 0,
        }),
        icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
        isDest: true,
        onConfirm: async () => {
          const success = await dispatch(deleteTag(tag.uuid) as any);
          if (success) {
            await loadTags();
          }
          dispatch(hideModal());
        },
        onCancel: () => {},
        showModal: true,
      }));
    } else {
      dispatch(showModal({
        type: 'confirmation',
        message: t('Delete Tag'),
        subMsg: t('Are you sure you want to delete the tag "{{tagName}}"? This cannot be undone.', {
          tagName: tag.name,
        }),
        icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
        isDest: true,
        onConfirm: async () => {
          const success = await dispatch(deleteTag(tag.uuid) as any);
          if (success) {
            await loadTags();
          }
          dispatch(hideModal());
        },
        onCancel: () => {},
        showModal: true,
      }));
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag || !editName.trim()) return;
    const result = await dispatch(updateTag(editingTag.uuid, { name: editName.trim(), color: editColor }) as any);
    if (result) {
      setEditingTag(null);
      await loadTags();
    }
  };

  const startEditing = (tag: OrgTag) => {
    setEditingTag(tag);
    setEditName(tag.name);
    setEditColor(tag.color);
  };

  return (
    <>
    <div className="space-y-6">
      {!isAdmin ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <PiShieldWarningLight className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('Admin Access Required')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
            {t('Only organization administrators can manage these settings. Contact your admin if you need changes.')}
          </p>
        </div>
      ) : (
      <>
      <div className="space-y-2 px-4 pt-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          {t('Organization Settings')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('Manage organization-wide settings for file management and storage.')}
        </p>
      </div>

      <hr className="border-gray-200 dark:border-gray-700" />

      {/* Recycle Bin Section */}
      <div data-tour-id="org-retention" className="grid grid-cols-2 2xl:grid-cols-3 gap-4 px-4 items-start">
        <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
          <div className="p-4">
            <div className="flex flex-col h-full">
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm mb-1">
                  {t('Recycle Bin Auto-delete Period')}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('How long deleted items remain in the recycle bin before being permanently deleted.')}
                </p>
              </div>
              <div className="mt-4">
                <Dropdown
                  options={RETENTION_OPTIONS}
                  value={retentionDays}
                  placeholder={t('Select auto-delete period')}
                  onChange={handleRetentionChange}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tags Management Section */}
      <div className="space-y-2 px-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          {t('Document Tags')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('Create and manage tags that can be applied to files and folders for classification.')}
        </p>
      </div>

      <hr className="border-gray-200 dark:border-gray-700" />

      <div data-tour-id="org-tags" className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 max-w-lg mx-4">
        <div className="p-4 space-y-4">
          {/* Create new tag */}
          <div className="space-y-3">
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t('Tag Name')}</label>
              <Input
                type="text"
                value={newTagName}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length > 50) return;
                  if (val && containsDisallowedCharacters(val)) {
                    setTagError(t('Tag name contains invalid characters'));
                    return;
                  }
                  setNewTagName(val);
                  if (tagError) setTagError('');
                }}
                placeholder={t('Enter tag name')}
                className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateTag()}
              />
              {tagError && (
                <p className="absolute left-0 top-full mt-0.5 text-xs text-red-500 dark:text-red-400">{tagError}</p>
              )}
              <span className={`absolute right-0 top-full mt-0.5 text-[10px] ${newTagName.length >= 45 ? 'text-amber-500' : 'text-gray-400'}`}>
                {newTagName.length}/50
              </span>
            </div>
            <div className="flex items-end gap-3 mt-1">
              <div className="w-40">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t('Color')}</label>
                <Dropdown
                  options={TAG_COLORS}
                  value={newTagColor}
                  onChange={(v) => setNewTagColor(Array.isArray(v) ? v[0] : v)}
                  className="w-full"
                  renderOption={(option: any) => (
                    <div className="flex items-center gap-2">
                      <Badge color={option.value as BadgeColor}>{option.label}</Badge>
                    </div>
                  )}
                />
              </div>
              {newTagName.trim() && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{t('Preview')}:</span>
                  <Badge color={newTagColor as BadgeColor}>{newTagName.trim()}</Badge>
                </div>
              )}
              <div className="ml-auto">
                <Button
                  onClick={handleCreateTag}
                  disabled={!newTagName.trim()}
                  icon={<PiPlusLight className="w-3.5 h-3.5" />}
                  size='small'
                  variant='primary'
                >
                  {t('Add')}
                </Button>
              </div>
            </div>
          </div>

          {/* Tag list */}
          <div className="space-y-1">
            {tags.length === 0 && (
              <p className="text-sm text-gray-400 py-2 text-center">{t('No tags created yet')}</p>
            )}
            {tags.map(tag => (
              <div key={tag.uuid} className="py-2 px-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800 group">
                {editingTag?.uuid === tag.uuid ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={editName}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val.length > 50) return;
                          if (val && containsDisallowedCharacters(val)) return;
                          setEditName(val);
                        }}
                        className="flex-1 text-sm px-2 py-1 rounded border border-gray-200 dark:border-gray-600 bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && handleUpdateTag()}
                      />
                      <Dropdown
                        options={TAG_COLORS}
                        value={editColor}
                        onChange={(v) => setEditColor(Array.isArray(v) ? v[0] : v)}
                        renderOption={(option: any) => (
                          <div className="flex items-center gap-2">
                            <Badge color={option.value as BadgeColor}>{option.label}</Badge>
                          </div>
                        )}
                        className="w-28"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge color={editColor as BadgeColor}>{editName || tag.name}</Badge>
                      <div className="ml-auto flex items-center gap-2">
                        <Button variant='primary' size='small' onClick={handleUpdateTag}>{t('Save')}</Button>
                        <Button variant='outline' size='small' onClick={() => setEditingTag(null)}>{t('Cancel')}</Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <Badge color={(tag.color as BadgeColor) || 'gray'}>{tag.name}</Badge>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => startEditing(tag)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                      >
                        <PiPencilSimpleLight className="w-3.5 h-3.5" />
                      </button>
                      {!tag.is_system && (
                        <button
                          onClick={() => handleDeleteTag(tag)}
                          className="p-1 text-gray-400 hover:text-red-500 rounded"
                        >
                          <PiTrashLight className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      </>
      )}
    </div>
    <FeatureTour
      steps={orgSettingsTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      disableOverlay
    />
    </>
  );
};

export default OrganizationSettings;
